import React from 'react';
import clsx from 'clsx';
import { IoIosCheckbox, IoIosSquareOutline, IoIosClose } from 'react-icons/io';
import { useGlobalDataContext } from '@context/GlobalData/GlobalDataContext';
import { cloneDeep } from 'lodash';
import { searchStates, FILTER_TYPES } from '@config';
import { translations } from '@utils/translations';

const Checkbox = ({
    title = '',
    isSelected,
    name,
    count = '',
    value,
    type = 'checkbox'
}) => {
    const { setGlobalContext } = useGlobalDataContext();

    const isRangeFilterType = FILTER_TYPES[title] === 'range';
    const isCheckboxType = type === 'checkbox';
    const isSelectionType = type === 'selection';

    const handleChange = () => {
        setGlobalContext(({ searchState, ...prevContext }) => {
            const { facets } = cloneDeep(prevContext);
            const facetExists = typeof facets[title] !== 'undefined';

            /**
             * In case the filter is used as selection "pill"
             * and is connected to a Range filter, then just remove the entire facet.
             */
            if (isRangeFilterType && isSelectionType && facetExists) {
                delete facets[title];
            }

            /**
             * Remove the value from the facet.
             * If the facet values are empty,
             * then remove the facet altogether.
             */
            if (isSelected && facetExists && !isRangeFilterType) {
                facets[title].values = facets[title].values.filter(
                    facetValue => facetValue !== value
                );

                if (facets[title].values.length === 0) {
                    delete facets[title];
                }
            }

            /**
             * Add the facet it it does not yet exist.
             */
            if (!isSelected && !facetExists) {
                facets[title] = {
                    values: [value]
                };
            }

            /**
             * If the facet does exist,
             * push the value to the start of the array.
             */
            if (!isSelected && facetExists) {
                facets[title].values = [value, ...facets[title].values];
            }

            return {
                ...prevContext,
                facets,
                start: 1,
                searchState: searchStates.UPDATING,
                prevSearchState: searchState
            };
        });
    };

    // Add the translation to the input field
    const translate = s => translations['nl'][s] || s;

    /**
     * The internals of the Checkbox component.
     *
     * @returns {JSX.Element}
     */
    const CheckboxComponent = () => (
        <li
            key={name}
            className={clsx(
                isCheckboxType && `op-filter__checkbox`,
                isSelectionType && `op-selection__badge`,
                isCheckboxType && isSelected && `op-filter__checkbox-active`,
                Number(count) === 0 && `op-filter__checkbox-disabled`
            )}
        >
            <label
                className={clsx(
                    isCheckboxType && `op-filter__checkbox-label`,
                    isSelectionType && `op-selection__badge-label`
                )}
            >
                <input
                    name={title}
                    style={{ display: 'none' }}
                    type="checkbox"
                    checked={isSelected}
                    onChange={handleChange}
                    data-value={value}
                    value={value}
                    className={clsx(
                        `op-filter__checkbox`,
                        isSelected && `op-filter__checkbox-active`
                    )}
                />

                {isCheckboxType &&
                    (isSelected ? <IoIosCheckbox /> : <IoIosSquareOutline />)}

                <span>{translate(name)}</span>

                {isCheckboxType && (
                    <span className={`op-filter__checkbox-count`}>{count}</span>
                )}

                {isSelectionType && <IoIosClose />}
            </label>
        </li>
    );

    if (isSelectionType) {
        return isSelected && <CheckboxComponent />;
    }

    if (isCheckboxType) {
        return <CheckboxComponent />;
    }

    return null;
};

export default Checkbox;
